import { cloneDeep } from '@graphcommerce/graphql'
import type { FilterEqualTypeInput } from '@graphcommerce/graphql-mesh'
import { ProductListFiltersFragment } from '@graphcommerce/magento-product/components/ProductListFilters/ProductListFilters.gql'
import { ProductListLink } from '@graphcommerce/magento-product/components/ProductListLink/ProductListLink'
import { useProductListParamsContext } from '@graphcommerce/magento-product/hooks/useProductListParamsContext'
import { ChipMenuProps, extendableComponent } from '@graphcommerce/next-ui'
import { Checkbox } from '@mui/material'
import { useState } from 'react'
import type { SetRequired } from 'type-fest'
import { useProductListLinkReplace } from '../../../hooks/Product/useProductListLinkReplace'
import { Icon } from '../../Layout/Icon'

type OwnerState = {
  isColor: boolean
  isActive: boolean
}
const componentName = 'FilterEqual' as const
const parts = [
  'listItem',
  'listItemInnerContainer',
  'checkbox',
  'linkContainer',
  'button',
  'resetButton',
  'filterAmount',
  'filterLabel',
  'isColor',
  'isActive',
] as const

const { classes, selectors, withState } = extendableComponent<
  OwnerState,
  typeof componentName,
  typeof parts
>(componentName, parts)

export type FilterIn = SetRequired<Omit<FilterEqualTypeInput, 'eq'>, 'in'>

type Filter = NonNullable<NonNullable<ProductListFiltersFragment['aggregations']>[number]>

type FilterEqualTypeProps = Filter & Omit<ChipMenuProps, 'selected'>

export function FilterEqualType(props: FilterEqualTypeProps) {
  const { attribute_code, count, label, options, __typename, ...chipProps } = props
  const { params } = useProductListParamsContext()
  const replaceRoute = useProductListLinkReplace({ scroll: false })
  const [isOpen, setIsOpen] = useState(true)

  const currentFilter: FilterEqualTypeInput = cloneDeep(params.filters[attribute_code]) ?? {
    in: [],
  }

  const anyFilterActive = Object.keys(params?.filters ?? {}).length >= 1

  const currentLabels =
    options
      ?.filter((option) => option && currentFilter.in?.includes(option.value))
      .map((option) => option && option.label) ?? []

  const removeFilter = () => {
    const linkParams = cloneDeep(params)
    delete linkParams.filters[attribute_code]
    delete linkParams.currentPage

    replaceRoute(linkParams)
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        type='button'
        className='mb-[10px] inline-flex w-full items-center justify-center'
      >
        <div className='flex w-full items-center justify-between'>
          <span className='Type-XL-Medium'>{label}</span>
          <span className='inline-flex h-[20px] w-[20px] items-center justify-center'>
            {isOpen ? (
              <Icon name='expand_less' className='text-4xl' />
            ) : (
              <Icon name='expand_more' className='text-4xl' />
            )}
          </span>
        </div>
      </button>
      {isOpen && (
        <div className='mb-[10px] pl-[30px]'>
          {options?.map((option) => {
            if (!option?.value) return null
            const labelId = `filter-equal-${attribute_code}-${option?.value}`
            const filters = cloneDeep(params.filters)
            const isColor = !!attribute_code?.toLowerCase().includes('color')
            const isActive = Boolean(
              isColor && currentFilter.in?.includes(option?.value) && isColor,
            )

            const cls = withState({ isColor, isActive })
            if (currentFilter.in?.includes(option.value)) {
              filters[attribute_code] = {
                ...currentFilter,
                in: currentFilter.in?.filter((v) => v !== option.value),
              }
            } else {
              filters[attribute_code] = {
                ...currentFilter,
                in: [...(currentFilter?.in ?? []), option.value],
              }
            }

            return (
              <ProductListLink
                {...params}
                filters={filters}
                currentPage={undefined}
                key={option?.value}
                color='inherit'
                link={{ replace: anyFilterActive, prefetch: false }}
                className='block'
              >
                <div className='flex items-center'>
                  <Checkbox checked={currentFilter.in?.includes(option?.value ?? '')} />
                  <p className='Type-XL-Regular'>{option?.label}</p>
                </div>
              </ProductListLink>
            )
          })}
        </div>
      )}
    </div>
  )
}

FilterEqualType.selectors = selectors
